import React, { useState } from 'react';

import { CollapsibleContentArrow, ARROW_DIRECTION_DOWN } from 'ui/collapsibleContent';
import { Link } from 'ui/links';
import { transformWorkflowWebhook } from '../../workflowWebhooks/utils';
import { CONTENT_TYPE_LABELS } from '../../workflowWebhooks/constants';

import { useWorkflowNodesForDisplay } from '../hooks';

import { FormUrlEncodedBody } from './FormUrlEncodedBody';
import { JsonBody } from './JsonBody';
import { WebhookBodyNotSet } from './WebhookBodyNotSet';

import {
  CollapsibleNameCell,
  Content,
  Details,
  Medium,
  CollapsibleItem,
  SecondaryCaption,
  StyledGridContainer,
  StyledCollapsibleContent,
  ArrowButton,
  WorkflowNodeEntry,
} from './WorkflowWebhookNameCell.styles';

export const WorkflowWebhookNameCell = ({ workflowWebhook }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { id, name, contentType, workflowNodes, body } = workflowWebhook;

  const workflows = useWorkflowNodesForDisplay(workflowNodes);

  const webhook = transformWorkflowWebhook(workflowWebhook);

  const mergedUserFields = [
    ...(webhook.includeDefaultFields ? ['workflowId', 'email'] : []),
    ...webhook.userFields.map((f) => f.field),
  ];

  const renderBody = () => {
    switch (contentType) {
      case 'Json':
        return <JsonBody body={webhook.jsBody} />;
      case 'FormUrlEncoded':
        return <FormUrlEncodedBody body={body} />;
      default:
        return <WebhookBodyNotSet />;
    }
  };
  return (
    <React.Fragment key={id}>
      <CollapsibleNameCell data-test={`workflow-webhook-${id}-name-cell`}>
        <Content data-test="workflow-webhook-content">
          <div>
            <Link
              inTable
              useReactRouter
              href={`/workflows/webhooks/${id}`}
              data-test="workflow-webhook-name"
            >
              {name}
            </Link>
          </div>
          <Details data-test="workflow-webhook-medium-row">
            <Medium data-test="workflow-webhook-medium">
              ID {id} ∙ {CONTENT_TYPE_LABELS[contentType]} ∙{' '}
              {workflowNodes ? workflowNodes.length : 0} journey tiles
            </Medium>
          </Details>
        </Content>
        <ArrowButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          data-test="collapsible-arrow-button"
        >
          <CollapsibleContentArrow
            isCollapsed={isCollapsed}
            arrowStart={ARROW_DIRECTION_DOWN}
            arrowEnd={180}
            width={16}
            height={16}
          />
        </ArrowButton>
      </CollapsibleNameCell>
      <StyledCollapsibleContent
        isCollapsed={isCollapsed}
        data-test={`collapsible-workflow-webhook-name${isCollapsed ? '-collapsed' : ''}`}
      >
        <StyledGridContainer>
          <CollapsibleItem data-test="workflow-webhook-body-detail">
            <SecondaryCaption>Body:</SecondaryCaption>
            <SecondaryCaption>{renderBody()}</SecondaryCaption>
          </CollapsibleItem>
          {mergedUserFields.length > 0 && (
            <CollapsibleItem data-test="workflow-webhook-body-detail">
              <SecondaryCaption>Additional fields:</SecondaryCaption>
              <SecondaryCaption>{mergedUserFields.join(', ')}</SecondaryCaption>
            </CollapsibleItem>
          )}
          <CollapsibleItem data-test="workflow-webhook-nodes-detail">
            <SecondaryCaption>Journey tiles:</SecondaryCaption>
            <SecondaryCaption>
              {Object.keys(workflows).length === 0
                ? 'None'
                : Object.keys(workflows).map((k) => {
                    const workflow = workflows[k];
                    return (
                      <WorkflowNodeEntry key={`workflow-${k}`}>
                        <Link inTable size="caption" href={`/workflows/${k}/edit`}>
                          {workflow.name}
                        </Link>{' '}
                        (Node IDs:{' '}
                        {workflow.nodes.map((nodeId, nodeIdx) => (
                          <span key={nodeId}>
                            {nodeId}
                            {nodeIdx === workflow.nodes.length - 1 ? '' : ', '}
                          </span>
                        ))}
                        ) &nbsp;
                      </WorkflowNodeEntry>
                    );
                  })}
            </SecondaryCaption>
          </CollapsibleItem>
        </StyledGridContainer>
      </StyledCollapsibleContent>
    </React.Fragment>
  );
};
